import { effect } from '@preact/signals-react';
import {
    CreateTradingViewTablePayload,
    CreateTablePayload,
    CreateTableByNlpPayload,
} from '@thinkalpha/platform-ws-client/contracts/table.js';
import type {
    TableCreationResultWithColumns,
    NlpTableCreationResult,
} from '@thinkalpha/platform-ws-client/contracts/table.js';
import type { RawClient } from '@thinkalpha/table-client';
import type { AxiosRequestConfig } from 'axios';
import { useCallback, useMemo, useSyncExternalStore } from 'react';
import { createInstance } from 'src/api';
import { useContainer } from 'src/features/ioc';

const api = createInstance({}, { useLegacySerialization: false });

function addDebugIdentifierToParams(
    params: AxiosRequestConfig['params'],
    debugIdentifier: string,
): AxiosRequestConfig['params'] {
    if (params instanceof URLSearchParams) {
        const nextParams = new URLSearchParams(params);
        nextParams.set('debug', debugIdentifier);
        return nextParams;
    }
    return {
        ...params,
        debug: debugIdentifier,
    };
}

/**
 * Best-effort adapter for low-value debugging surfaces that access the raw client directly. Product
 * tables should react to the target through TableModel instead.
 */
export const usePureProxyClient = (): RawClient => {
    const container = useContainer();
    const proxyClientService = useMemo(() => container.get('ProxyClientService'), [container]);
    // Preserve basic hot-switch behavior for admin/debugging tools without making their individual
    // resource lifecycles part of the product hot-switch contract.
    const subscribe = useCallback(
        (onStoreChange: () => void) => {
            return effect(() => {
                void proxyClientService.target.value;
                onStoreChange();
            });
        },
        [proxyClientService],
    );
    const getSnapshot = useCallback(() => proxyClientService.target.value.client, [proxyClientService]);
    return useSyncExternalStore(subscribe, getSnapshot);
};

export const createTable = async (
    payload: CreateTablePayload,
    config?: AxiosRequestConfig,
    debugIdentifier?: string,
): Promise<TableCreationResultWithColumns> => {
    const encoded = CreateTablePayload.encode(payload);
    const requestConfig = debugIdentifier
        ? {
              ...config,
              params: addDebugIdentifierToParams(config?.params, debugIdentifier),
          }
        : config;
    const res = await api.post<TableCreationResultWithColumns>(`/tables`, encoded, requestConfig);
    return res.data;
};

export const createTableFromNLP = async (
    payload: CreateTableByNlpPayload,
    config?: AxiosRequestConfig,
): Promise<NlpTableCreationResult> => {
    const encoded = CreateTableByNlpPayload.encode(payload);
    const res = await api.post<NlpTableCreationResult>(`/tables/by-nlp`, encoded, config);
    return res.data;
};

export const createTradingViewTable = async (
    payload: CreateTradingViewTablePayload,
    config?: AxiosRequestConfig,
): Promise<TableCreationResultWithColumns> => {
    const encoded = CreateTradingViewTablePayload.encode(payload);
    const res = await api.post<TableCreationResultWithColumns>(`/tables/trading-view`, encoded, config);
    return res.data;
};
